import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Square, Bug, CheckCircle2, AlertCircle, Wifi, ArrowRight } from 'lucide-react';
import { Actuator, TestModeState } from '../../types/actuators';
import { Modal } from '../UI/Modal';
import { cn } from '@/utils/cn';
import {
  PeristalticPumpIcon,
  SolenoidValveIcon,
  HeaterIcon,
  GrowLightIcon,
  MixerIcon,
  CoolingValveIcon,
  PumpIcon,
  OzonizerIcon,
} from '../UI/Icons';

const getActuatorIcon = (actuator: Actuator) => {
  const id = actuator.id.toLowerCase();
  const name = actuator.name.toLowerCase();

  if (
    id.includes('peristal') ||
    name.includes('peristal') ||
    id.includes('ph_plus') ||
    id.includes('ph_minus') ||
    id.includes('nutrient')
  ) {
    return <PeristalticPumpIcon className="w-full h-full" scale={1.6} />;
  }
  if (id.includes('cooling') || id.includes('chlaz')) {
    return <CoolingValveIcon className="w-full h-full" />;
  }
  if (id.includes('solenoid') || name.includes('solenoid') || actuator.category === 'valve') {
    return <SolenoidValveIcon className="w-full h-full" />;
  }
  if (
    id.includes('circulation') ||
    id.includes('suction') ||
    name.includes('obehov') ||
    name.includes('sací')
  ) {
    return <PumpIcon className="w-full h-full" scale={1.6} />;
  }
  if (actuator.category === 'mixer' || id.includes('mixer') || id.includes('mich')) {
    return <MixerIcon className="w-full h-full" />;
  }
  if (actuator.category === 'heater' || id.includes('heater') || name.includes('topení')) {
    return <HeaterIcon className="w-full h-full" />;
  }
  if (actuator.category === 'light' || id.includes('light') || name.includes('osvětlení')) {
    return <GrowLightIcon className="w-full h-full" />;
  }
  if (id.includes('ozon') || name.includes('ozon')) {
    return <OzonizerIcon className="w-full h-full" />;
  }
  return <span className="text-[11px] font-bold">HW</span>;
};

const getActuatorTone = (actuator: Actuator): keyof typeof ACTUATOR_TONE_CLASSES => {
  const id = actuator.id.toLowerCase();
  if (id.includes('ph_minus')) return 'red';
  if (id.includes('ph_plus')) return 'cyan';
  if (id.includes('nutrient_a')) return 'teal';
  if (id.includes('nutrient_b')) return 'emerald';
  if (actuator.category === 'pump') return 'cyan';
  if (actuator.category === 'valve') return 'emerald';
  if (actuator.category === 'heater') return 'red';
  if (actuator.category === 'light') return 'amber';
  if (actuator.category === 'mixer') return 'teal';
  return 'gray';
};

const ACTUATOR_TONE_CLASSES = {
  cyan: {
    selectedBorder: 'border-cyan-500 dark:border-cyan-400',
    idleIcon: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-300',
    selectedIcon: 'bg-cyan-500 text-white',
    activeBadge: 'bg-cyan-500/15 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300',
  },
  emerald: {
    selectedBorder: 'border-emerald-500 dark:border-emerald-400',
    idleIcon: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300',
    selectedIcon: 'bg-emerald-500 text-white',
    activeBadge: 'bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  },
  red: {
    selectedBorder: 'border-red-500 dark:border-red-400',
    idleIcon: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300',
    selectedIcon: 'bg-red-500 text-white',
    activeBadge: 'bg-red-500/15 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  },
  amber: {
    selectedBorder: 'border-amber-500 dark:border-amber-400',
    idleIcon: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300',
    selectedIcon: 'bg-amber-500 text-white',
    activeBadge: 'bg-amber-500/15 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  },
  teal: {
    selectedBorder: 'border-teal-500 dark:border-teal-400',
    idleIcon: 'bg-teal-50 text-teal-700 dark:bg-teal-500/10 dark:text-teal-300',
    selectedIcon: 'bg-teal-500 text-white',
    activeBadge: 'bg-teal-500/15 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300',
  },
  gray: {
    selectedBorder: 'border-gray-400 dark:border-zinc-500',
    idleIcon: 'bg-gray-100 text-gray-600 dark:bg-zinc-700 dark:text-zinc-300',
    selectedIcon: 'bg-gray-500 text-white',
    activeBadge: 'bg-gray-500/15 text-gray-700 dark:bg-white/10 dark:text-zinc-300',
  },
} as const;

interface TestModeWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  actuators: Actuator[];
  testModeState: TestModeState | null;
  onStart: (actuatorIds?: string[]) => Promise<TestModeState | null>;
  onStop: () => Promise<void>;
  clearTestMode: () => void;
}

export const TestModeWizardModal: React.FC<TestModeWizardModalProps> = ({
  isOpen,
  onClose,
  actuators,
  testModeState,
  onStart,
  onStop,
  clearTestMode,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedActuatorId, setSelectedActuatorId] = useState<string>('all');
  const [isStarting, setIsStarting] = useState(false);

  const isRunning =
    testModeState?.status === 'running' ||
    testModeState?.status === 'waiting_ack';
  const isWaitingForFirmware = testModeState?.status === 'waiting_ack';
  const isCompleted = testModeState?.status === 'completed' || testModeState?.status === 'aborted';

  const currentActuator = testModeState?.currentActuatorId
    ? actuators.find((a) => a.id === testModeState.currentActuatorId)
    : actuators[testModeState?.currentActuatorIndex ?? 0];

  // Auto-advance logic based on API state
  useEffect(() => {
    if (isRunning && step === 1) {
      setStep(2);
    }
    if (isCompleted && step === 2) {
      setStep(3);
    }
  }, [isRunning, isCompleted, step]);

  // Reset state when opening modal
  useEffect(() => {
    if (isOpen) {
      if (isCompleted) {
        clearTestMode();
      }
      if (!isRunning) {
        setStep(1);
      }
    }
  }, [isOpen]);

  const handleStart = async () => {
    setIsStarting(true);
    try {
      await onStart(selectedActuatorId === 'all' ? undefined : [selectedActuatorId]);
      setStep(2);
    } finally {
      setIsStarting(false);
    }
  };

  const handleStop = async () => {
    await onStop();
    setStep(3);
  };

  const handleClose = () => {
    if (isRunning) {
      const confirmClose = window.confirm('Test stále běží. Opravdu chcete okno zavřít? (Test bude pokračovat na pozadí)');
      if (!confirmClose) return;
    }
    onClose();
  };

  // ------------------------------------------------------------------
  // UI STEPS
  // ------------------------------------------------------------------
  
  const renderStep1 = () => (
    <motion.div
      key="step1"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="space-y-6"
    >
      <div className="text-center">
        <Bug className="w-10 h-10 text-cyan-500 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          Jdeme na diagnostiku!
        </h3>
        <p className="text-sm text-gray-500 dark:text-zinc-400 max-w-sm mx-auto">
          Průvodce postupně sepne vybrané fyzické komponenty.
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-4 text-center">
          Která zařízení chcete otestovat?
        </label>
        
        <div className="w-full pb-2 max-h-[45vh] overflow-y-auto custom-scrollbar pr-2">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            <button
              onClick={() => setSelectedActuatorId('all')}
              className={`flex flex-col items-center justify-center p-2 sm:p-3 rounded-lg border-2 transition-all ${
                selectedActuatorId === 'all'
                  ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20 shadow-sm'
                  : 'border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 hover:border-cyan-300 hover:bg-gray-50 dark:hover:bg-zinc-700/50'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1.5 ${
                selectedActuatorId === 'all' ? 'bg-cyan-500 text-white' : 'bg-gray-100 dark:bg-zinc-700 text-gray-500 dark:text-zinc-400'
              }`}>
                <Play className="w-4 h-4" />
              </div>
              <span className="text-[11px] font-bold text-center text-gray-900 dark:text-white leading-tight uppercase tracking-wider">
                Vše
              </span>
            </button>
            
            {actuators.map((a) => {
              const tone = ACTUATOR_TONE_CLASSES[getActuatorTone(a)];
              const isSelected = selectedActuatorId === a.id;
              
              return (
                <button
                  key={a.id}
                  onClick={() => setSelectedActuatorId(a.id)}
                  className={`flex flex-col items-center justify-center p-2 sm:p-3 rounded-lg border-2 transition-all ${
                    isSelected
                      ? `bg-cyan-50 dark:bg-cyan-900/20 shadow-sm ${tone.selectedBorder}`
                      : 'border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 hover:bg-gray-50 dark:hover:bg-zinc-700/50 hover:border-gray-300 dark:hover:border-zinc-600'
                  }`}
                >
                  <div
                    className={cn(
                      'mb-1.5 flex h-8 w-8 items-center justify-center rounded-full shadow-sm',
                      isSelected ? tone.selectedIcon : tone.idleIcon,
                    )}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      {getActuatorIcon(a)}
                    </div>
                  </div>
                  <span className="text-[11px] font-medium text-center text-gray-700 dark:text-zinc-300 line-clamp-2 leading-tight">
                    {a.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex justify-center mt-6">
        <button
          onClick={handleStart}
          disabled={isStarting || actuators.length === 0}
          className="inline-flex items-center gap-2 px-8 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl font-medium shadow-lg hover:shadow-cyan-500/25 transition-all focus:ring-4 focus:ring-cyan-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isStarting ? (
            <div className="animate-spin h-5 w-5 border-2 border-white/40 border-t-white rounded-full" />
          ) : (
            <>
              <span>Zahájit testování</span>
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div
      key="step2"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="space-y-6"
    >
      {(() => {
        const progressScale = Math.min(Math.max((testModeState?.progress ?? 0) / 100, 0), 1);
        const currentTone = currentActuator
          ? ACTUATOR_TONE_CLASSES[getActuatorTone(currentActuator)]
          : ACTUATOR_TONE_CLASSES.gray;

        return (
          <>
      <div className="text-center">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          Probíhá testování...
        </h3>
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          Zařízení {testModeState?.currentActuatorIndex !== undefined ? testModeState.currentActuatorIndex + 1 : 1} z {testModeState?.totalActuators || 1}
        </p>
      </div>

      {/* Progress Bar */}
      <div className="bg-gray-100 dark:bg-zinc-800 rounded-full h-3 w-full overflow-hidden shadow-inner">
        <motion.div
          className="relative h-full w-full origin-left bg-cyan-500 transition-all duration-300"
          initial={false}
          animate={{ scaleX: progressScale }}
          transition={{ duration: 0.3 }}
        >
          <div className="absolute inset-0 bg-white/20 animate-pulse" />
        </motion.div>
      </div>

      {/* Current Actuator Card */}
      {currentActuator && (
        <div
          className={`p-6 rounded-xl border-2 text-center transition-colors ${
            isWaitingForFirmware
              ? 'bg-yellow-500/10 border-yellow-500/50'
              : 'bg-cyan-500/10 border-cyan-500/50'
          }`}
        >
          <div className="flex justify-center mb-4">
            <div
              className={cn(
                'flex h-16 w-16 items-center justify-center rounded-full',
                currentTone.activeBadge,
              )}
            >
              {getActuatorIcon(currentActuator)}
            </div>
          </div>
          
          <h4 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
            {currentActuator.name}
          </h4>

          {/* Status feedback */}
          <div className="flex items-center justify-center gap-3">
            {isWaitingForFirmware ? (
              ((currentActuator?.category as string) === 'light' || (currentActuator?.category as string) === 'lighting') ? (
                <>
                  <div className="animate-pulse flex-shrink-0 h-8 w-8 border-4 border-yellow-500/50 rounded-full" />
                  <span className="text-gray-600 dark:text-zinc-300 font-medium text-sm text-left leading-tight max-w-[250px]">
                    Probíhá plynulá zkouška stmívání. Bude trvat cca 100 vteřin, čekejte prosím...
                  </span>
                </>
              ) : (
                <>
                  <div className="animate-spin flex-shrink-0 h-8 w-8 border-4 border-yellow-500/30 border-t-yellow-500 rounded-full" />
                  <span className="text-gray-600 dark:text-zinc-300 font-medium">
                    Čekám na potvrzení (ACK) z firmware...
                  </span>
                </>
              )
            ) : (
              <>
                <Wifi className="w-8 h-8 text-cyan-500" />
                <span className="text-gray-600 dark:text-zinc-300 font-medium">
                  Příkaz odesílán do zařízení
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Stop test button */}
      <div className="flex justify-center mt-6">
        <button
          onClick={handleStop}
          className="inline-flex items-center gap-2 px-6 py-2.5 border border-red-500 text-red-500 hover:bg-red-500 hover:text-white rounded-xl font-medium transition-colors focus:ring-4 focus:ring-red-500/20"
        >
          <Square className="w-5 h-5" />
          <span>Přerušit testování</span>
        </button>
      </div>
          </>
        );
      })()}
    </motion.div>
  );

  const renderStep3 = () => {
    const isAborted = testModeState?.status === 'aborted';
    
    return (
      <motion.div
        key="step3"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        className="space-y-6"
      >
        <div className="text-center">
          <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4 ${
            isAborted ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-500' : 'bg-green-100 dark:bg-green-900/30 text-green-500'
          }`}>
            {isAborted ? <AlertCircle className="w-8 h-8" /> : <CheckCircle2 className="w-8 h-8" />}
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {isAborted ? 'Test byl přerušen' : 'Testování dokončeno!'}
          </h3>
          <p className="text-sm text-gray-500 dark:text-zinc-400">
            Fyzická rozsvícení a zapnutí byste měli reálně vidět v boxu.
          </p>
        </div>

        {/* Results Overview */}
        {testModeState?.results && testModeState.results.length > 0 && (
          <div className="bg-gray-50 dark:bg-zinc-800/50 rounded-xl p-4 mt-6 border border-gray-100 dark:border-zinc-800">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm uppercase tracking-wider">Výsledky komunikace</h4>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
              {testModeState.results.map((r, i) => {
                const actuator = actuators.find((a) => a.id === r.actuatorId);
                const isError = (r.firmwareResponse || 'timeout') !== 'success';
                
                return (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-white dark:bg-zinc-800 rounded-lg border border-gray-100 dark:border-zinc-700">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${isError ? 'bg-red-500' : 'bg-green-500'}`} />
                      <span className="font-medium text-sm text-gray-700 dark:text-zinc-300">
                        {actuator ? actuator.name : r.actuatorId}
                      </span>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      isError ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400' : 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400'
                    }`}>
                      {(r.firmwareResponse || 'timeout').toUpperCase()}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex justify-center mt-8">
          <button
            onClick={onClose}
            className="px-8 py-3 bg-gray-900 dark:bg-white text-white dark:text-zinc-900 hover:bg-gray-800 dark:hover:bg-zinc-100 rounded-xl font-medium shadow-lg transition-colors focus:ring-4 focus:ring-gray-500/20"
          >
            Hotovo, zavřít
          </button>
        </div>
      </motion.div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Průvodce testováním" size="xl">
      <Modal.Body className="max-h-[85vh] overflow-y-auto p-4 sm:p-6 custom-scrollbar">
        <AnimatePresence mode="wait">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </AnimatePresence>
      </Modal.Body>
    </Modal>
  );
};

export default TestModeWizardModal;
